/**
 * Eric 2026-06-25 (iter124 — Session A6) — Sous-onglet Fusionnée (Merged)
 * de la transcription Whisper+Fireflies d'une vidéo.
 *
 * Extrait de VideoDetailPage.js dans le cadre du refacto P0 (-800 lignes).
 * Le plus gros panneau du fichier : contient la liste des segments fusionnés,
 * les corrections d'orthographe / URL / ponctuation proposées par GPT-5.2,
 * les badges de provenance (whisper/fireflies/combined), les editions inline
 * de speakerMap, et toutes les actions associées (accept/reject correction,
 * bulk apply minors, restore rejected, etc.).
 *
 * Gating : `canEdit` (calculé côté parent = canEditContent(user)). Vincent #355
 * (SPEC STRICK) — Apprenant voit la fusion en lecture seule, sans contrôles.
 *
 * IMPORTANT : ce composant reçoit ~40 props (states partagés + handlers du
 * parent). C'est intentionnel : ces états sont aussi consommés par d'autres
 * sous-onglets (Whisper toolbar pour urlValidating, FirefliesSubTab pour
 * crossValidating). On garde une source unique côté parent pour éviter les
 * désynchronisations.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import { formatSpeakerLines } from "../../lib/speakerFormat";
import { diffMots } from "../../lib/diffMots";
import WebCorrectionBadge from "./WebCorrectionBadge";
import {
  SpinnerGap, ArrowClockwise, CheckCircle, Microphone, PencilSimple,
} from "@phosphor-icons/react";

export const MergedSubTab = ({ show, canEdit, lang, api, videoId,
  allTranscripts, transcriptsLoading,
  crossValidating, crossValidateResult,
  fusionAvailable, fusionBlockedReason,
  urlValidating, urlProposals,
  urlApplying, urlDropdownSelections,
  punctuationLoading, punctuationProposal,
  rejectedCorrections, editingCorrection,
  bulkMinorBusy,
  speakerMap,
  setCrossValidating, setCrossValidateResult,
  setUrlValidating, setUrlProposals,
  setUrlApplying, setUrlDropdownSelections,
  setPunctuationLoading, setPunctuationProposal,
  setAcceptedPunctMarks, setRejectedPunctMarks,
  setRejectedCorrections, setEditingCorrection,
  setBulkMinorBusy,
  loadVideoData, loadAllTranscripts,
  pollMergedTranscript,
  seekTo, formatTimeBracket,
}) => {
  // Vincent B766 (Eric 2026-02-17) — Focus segment via CustomEvent
  // depuis TranscriptPanel (bouton "✎ Corriger" dans l'aperçu Fusion).
  // Le segment ciblé est brièvement mis en surbrillance et scrollé en vue.
  const [focusedIdx, setFocusedIdx] = useState(null);
  // Vincent B936 rev2 (Eric 2026-02-26 SPEC STRICK PROD) — Suite au
  // remake du flow « Appliquer partout » : remplace le toast avec
  // action-button (B883/B884) par une checkbox INLINE placée sous
  // chaque correction acceptée/rejetée qui a des occurrences dupliquées.
  // `applyAllProposals` mappe `cKey` → `{mode: 'accept'|'reject', others}`.
  // Réinitialisé quand la checkbox est cochée (action déclenchée) OU
  // quand l'utilisateur re-toggle l'action originale (unaccept/unreject).
  const [applyAllProposals, setApplyAllProposals] = useState({});
  const [applyAllBusy, setApplyAllBusy] = useState({});
  // Vincent B891 (Eric 2026-02-14 SPEC STRICK PROD) — Ensemble d'IDs de
  // segments à highlighter en jaune quand l'utilisateur clique « Revoir »
  // sur la bannière de couverture partielle (B887). On sélectionne les
  // ~5 derniers segments (proches de la frontière du contenu manquant)
  // pour que Vincent voie D'UN COUP D'ŒIL où la fusion s'arrête et
  // pourquoi la couverture est partielle. Auto-clear après 4 s.
  const [coverageHighlightSegIds, setCoverageHighlightSegIds] = useState(null);
  // Bogue 888 (Vincent 2026-06-25, à refaire) — L'ancienne version reposait sur
  // l'attribut `title` natif : il n'apparaissait qu'après une seconde de survol,
  // sans indice visuel, et il affichait la même provenance pour TOUS les
  // segments (déduite de `video.fusion_sources`). Vincent l'a donc signalé comme
  // absent. Désormais : une pastille de couleur visible sur chaque timecode +
  // une infobulle instantanée, alimentées par `seg.source` (provenance calculée
  // segment par segment côté serveur), avec repli sur la provenance de la vidéo
  // pour les fusions antérieures à ce correctif.
  const SEGMENT_SOURCE_META = {
    fused: {
      fr: "Source : fusionnée (Whisper + Fireflies)",
      en: "Source: fused (Whisper + Fireflies)",
      dot: "bg-teal-500",
    },
    fireflies: {
      fr: "Source : Fireflies seul",
      en: "Source: Fireflies only",
      dot: "bg-violet-500",
    },
    manuel: {
      // Tâche 1253 — Un passage écrit à la main ne doit JAMAIS se fondre dans
      // le texte automatique : on doit toujours savoir qui a écrit quoi.
      fr: "Source : ajout manuel",
      en: "Source: manual addition",
      dot: "bg-amber-500",
    },
    whisper: {
      fr: "Source : Whisper seul",
      en: "Source: Whisper only",
      dot: "bg-sky-500",
    },
    unknown: {
      fr: "Source inconnue",
      en: "Unknown source",
      dot: "bg-zinc-300",
    },
  };
  const resolveSegmentSource = (seg) => {
    const own = String(seg?.source || "").toLowerCase();
    if (SEGMENT_SOURCE_META[own] && own !== "unknown") return own;
    const fs = allTranscripts?.video?.fusion_sources
      || (allTranscripts?.merged?.fusion_diagnostic?.fireflies_segments_available
          ? ["whisper", "fireflies"]
          : null);
    const hasW = fs ? fs.includes("whisper") : !!allTranscripts?.summary?.has_whisper;
    const hasFF = fs ? fs.includes("fireflies") : !!allTranscripts?.summary?.has_fireflies;
    if (hasW && hasFF) return "fused";
    if (hasFF) return "fireflies";
    if (hasW) return "whisper";
    return "unknown";
  };
  // Tâche 1175 (Eric 2026-06-25) — Filtre par provenance.
  const [sourceFilter, setSourceFilter] = useState("all");
  // Tâche 1332 (Eric 2026-06-30) — Réécriture d'une phrase à la main.
  const [editingSegId, setEditingSegId] = useState(null);
  const [segDraft, setSegDraft] = useState("");
  const [segSaving, setSegSaving] = useState(false);
  const saveSentence = async (seg) => {
    const texte = segDraft.trim();
    if (!texte || segSaving) return;
    setSegSaving(true);
    try {
      await axios.patch(
        `${api}/api/videos/${videoId}/merged-segments/${seg.id}/text`,
        { text: texte },
        { withCredentials: true },
      );
      toast.success(lang === "fr" ? "Phrase corrigée." : "Sentence updated.");
      setEditingSegId(null);
      setSegDraft("");
      if (typeof loadAllTranscripts === "function") await loadAllTranscripts(seg.id);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message
        || (lang === "fr" ? "Erreur inconnue" : "Unknown error");
      toast.error((lang === "fr" ? "Correction impossible : " : "Edit failed: ") + detail);
    } finally {
      setSegSaving(false);
    }
  };
  // Tâche 1274 — Les trois bandeaux d'information sur les corrections se
  // replient d'un clic (triangle sur la première ligne).
  const [correctionBannersOpen, setCorrectionBannersOpen] = useState(true);
  const mergedSegments = allTranscripts?.merged?.segments || [];
  // Passages que Whisper n'a PAS couverts : en fusion à deux sources, la
  // fusion part des segments Whisper, donc un intervalle vu uniquement par
  // Fireflies n'apparaît nulle part. On le reconstitue ici, en lecture seule,
  // pour que le filtre « Fireflies seul » réponde vraiment à la question.
  const firefliesOnlyGaps = (() => {
    const ffSegs = (allTranscripts?.fireflies?.segments || []).filter(
      (s) => Number.isFinite(Number(s?.start)) && Number.isFinite(Number(s?.end)),
    );
    if (!ffSegs.length || !mergedSegments.length) return [];
    const covered = mergedSegments
      .filter((s) => Number.isFinite(Number(s?.start)) && Number.isFinite(Number(s?.end)))
      .map((s) => [Number(s.start), Number(s.end)]);
    if (!covered.length) return [];
    return ffSegs.filter(
      (s) => !covered.some(([a, b]) => Math.min(Number(s.end), b) - Math.max(Number(s.start), a) > 0.25),
    );
  })();
  const segmentSourceCounts = (() => {
    const counts = { fused: 0, whisper: 0, fireflies: 0, unknown: 0 };
    mergedSegments.forEach((seg) => {
      counts[resolveSegmentSource(seg)] += 1;
    });
    counts.fireflies += firefliesOnlyGaps.length;
    counts.all = mergedSegments.length + firefliesOnlyGaps.length;
    return counts;
  })();

  // Bogue 891 REFAIT (Vincent 2026-06-25) — « BOUTON NON VISIBLE ».
  // Cause réelle : le bouton « Revoir » vivait UNIQUEMENT dans la bannière
  // d'alerte B887, qui n'apparaît que si `content_coverage_alert === true`.
  // Or les fusions de production n'ont même pas de `fusion_diagnostic`
  // (anciennes fusions, fusions mono-source) : ni bannière, ni badge B892,
  // donc aucun bouton, jamais. On recalcule donc la couverture côté client à
  // partir des segments réellement affichés, et le bouton « Revoir » vit
  // désormais sur le badge de couverture PERMANENT.
  const coverageInfo = (() => {
    const d = allTranscripts?.merged?.fusion_diagnostic || {};
    const lastEnd = (arr) => (arr || []).reduce(
      (m, s) => Math.max(m, Number(s?.end) || 0), 0,
    );
    const mergedEnd = d.merged_last_end_sec ?? lastEnd(mergedSegments);
    const srcEnd = d.source_last_end_sec ?? Math.max(
      lastEnd(allTranscripts?.whisper?.segments),
      lastEnd(allTranscripts?.fireflies?.segments),
      Number(allTranscripts?.video?.duration) || 0,
    );
    const ratio = d.merged_coverage_ratio
      ?? (srcEnd > 0 ? Math.min(1, mergedEnd / srcEnd) : null);
    return {
      mergedEnd,
      srcEnd,
      gapSec: Math.max(0, srcEnd - mergedEnd),
      ratio,
      // `estimated` = calculé côté client, faute de diagnostic serveur.
      estimated: d.merged_coverage_ratio == null,
    };
  })();

  // Bogue 891 — Action partagée par la bannière B887 et le badge permanent.
  const reviewCoverageBoundary = () => {
    // Vincent B891 (Eric 2026-02-14 SPEC STRICK PROD) —
    // Highlight jaune sur les segments proches de la
    // frontière du contenu manquant. On prend les
    // ~5 derniers segments (avec un plancher à 10 %
    // du total pour les longues vidéos) — ce sont
    // ceux juste avant que la fusion s'arrête, donc
    // les plus utiles pour comprendre pourquoi.
    const segs = (allTranscripts?.merged?.segments || []);
    if (segs.length === 0) return;
    const n = Math.max(1, Math.min(5, Math.ceil(segs.length * 0.1)));
    const tail = segs.slice(-n);
    setCoverageHighlightSegIds(new Set(tail.map(s => s.id)));
    // Scroll vers le PREMIER segment surligné pour
    // que Vincent voie la frontière du contenu
    // manquant, pas juste la fin absolue.
    const firstId = tail[0]?.id;
    setTimeout(() => {
      const container = document.querySelector('[data-testid="merged-container"]');
      const target = firstId
        ? container?.querySelector(`[data-seg-id="${firstId}"]`)
        : null;
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
      }
    }, 50);
  };
  useEffect(() => {
    if (!coverageHighlightSegIds) return;
    const t = setTimeout(() => setCoverageHighlightSegIds(null), 4200);
    return () => clearTimeout(t);
  }, [coverageHighlightSegIds]);
  useEffect(() => {
    const onFocus = (ev) => {
      const detail = ev && ev.detail ? ev.detail : {};
      if (detail.videoId && detail.videoId !== videoId) return;
      const idx = Number(detail.segmentIndex);
      if (!Number.isFinite(idx) || idx < 0) return;
      setFocusedIdx(idx);
      // Scroll après le prochain paint pour laisser React monter les segments.
      setTimeout(() => {
        try {
          const el = document.querySelector(`[data-testid="merged-segment-${idx}"]`);
          if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        } catch (_e) { /* silent */ }
      }, 80);
      // Retire la surbrillance après 2.5s.
      // Tâche 1338 — Sauf quand la surbrillance sert de curseur de relecture
      // (flèches, raccourcis Alt) : elle doit survivre jusqu'au saut suivant,
      // sinon Alt + Entrée ne sait plus quelle correction accepter.
      if (!detail?.persist) {
        setTimeout(() => setFocusedIdx((cur) => (cur === idx ? null : cur)), 2500);
      }
    };
    window.addEventListener("knoria:merged-focus-segment", onFocus);
    return () => window.removeEventListener("knoria:merged-focus-segment", onFocus);
  }, [videoId]);

  // Tâche 1338 (Eric 2026-06-30) — Alt + Entrée accepte la correction en
  // surbrillance. Relire cent phrases obligeait sinon à viser une case de
  // 24 pixels à la souris, ligne après ligne.
  useEffect(() => {
    if (!show || !canEdit) return undefined;
    const surTouche = async (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      // Tâche 1350 (Eric 2026-06-30) — Refuser au clavier. Sans cela, une
      // relecture au clavier s'arrêtait net dès la première correction à
      // rejeter : il fallait reprendre la souris pour une ligne sur trois.
      const refus = e.key === "Backspace" || e.key === "Delete";
      if (e.key !== "Enter" && !refus) return;
      const balise = (e.target?.tagName || "").toLowerCase();
      if (balise === "input" || balise === "textarea" || e.target?.isContentEditable) return;
      if (focusedIdx === null) return;
      const seg = (allTranscripts?.merged?.segments || [])[focusedIdx];
      if (!seg) return;
      const corr = (seg.corrections || []).find((c) => !c.accepted && !c.rejected);
      e.preventDefault();
      if (!corr) {
        toast.info(lang === "fr"
          ? "Aucune correction en attente sur cette phrase."
          : "No pending correction on this sentence.");
        return;
      }
      try {
        await axios.post(
          `${api}/api/videos/${videoId}/${refus ? "reject-correction" : "accept-correction"}`,
          { segment_id: seg.id, original: corr.original, correction: corr.correction },
          { withCredentials: true },
        );
        toast.success(refus
          ? (lang === "fr" ? "Correction refusée" : "Correction rejected")
          : (lang === "fr" ? "Correction acceptée" : "Correction accepted"));
        loadAllTranscripts(seg.id);
        // Tâche 1350 — Un refus fait avancer la relecture guidée tout comme
        // une acceptation : dans les deux cas, la ligne est traitée.
        window.dispatchEvent(new CustomEvent("knoria:merged-correction-accepted", {
          detail: { videoId, segmentId: seg.id, rejected: refus },
        }));
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message
          || (lang === "fr" ? "Erreur inconnue" : "Unknown error");
        toast.error(((refus
          ? (lang === "fr" ? "Refus échoué : " : "Reject failed: ")
          : (lang === "fr" ? "Acceptation échouée : " : "Accept failed: "))) + detail);
      }
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [show, canEdit, focusedIdx, allTranscripts, api, videoId, lang, loadAllTranscripts]);
  if (!show) return null;
  return (
                  <div className="max-h-[calc(100vh-320px)] min-h-[300px] overflow-y-auto" data-testid="merged-container">
                    {transcriptsLoading ? (
                      <div className="p-10 text-center text-zinc-400">
                        <SpinnerGap size={28} className="mx-auto mb-3 animate-spin opacity-50" />
                        <p className="text-sm">{lang === "fr" ? "Chargement..." : "Loading..."}</p>
                      </div>
                    ) : !allTranscripts?.merged ? (
                      <div className="p-10 text-center text-zinc-400" data-testid="merged-empty">
                        {crossValidating || crossValidateResult ? (
                          <>
                            <SpinnerGap size={36} className="mx-auto mb-3 animate-spin text-[#002FA7]" />
                            <p className="text-sm text-zinc-700 font-medium">
                              {lang === "fr" ? "Fusion en cours…" : "Merging in progress…"}
                            </p>
                            <p className="text-xs mt-2 text-zinc-500">
                              {lang === "fr"
                                ? "GPT-5.2 compare Whisper et Fireflies. La fusion peut prendre 1 à 3 minutes selon la longueur."
                                : "GPT-5.2 is comparing Whisper and Fireflies. The merge takes 1 to 3 minutes depending on length."}
                            </p>
                            <button
                              onClick={() => loadAllTranscripts()}
                              className="mt-4 text-xs text-[#002FA7] hover:underline"
                              data-testid="refresh-merge-btn"
                            >
                              {lang === "fr" ? "Rafraîchir maintenant" : "Refresh now"}
                            </button>
                          </>
                        ) : allTranscripts?.fireflies ? (
                          <>
                            <ArrowClockwise size={36} className="mx-auto mb-3 opacity-30" />
                            <p className="text-sm">
                              {lang === "fr"
                                ? "Transcription Fireflies importée, mais aucune fusion enregistrée. La fusion s'exécute en arrière-plan après l'import — Patientez ou cliquez ci-dessous pour rafraîchir."
                                : "Fireflies transcript imported, but no merge saved yet. Merging runs in the background after import — wait or click below to refresh."}
                            </p>
                            <button
                              onClick={() => loadAllTranscripts()}
                              className="mt-3 text-xs text-[#002FA7] hover:underline"
                              data-testid="refresh-merge-btn"
                            >
                              {lang === "fr" ? "Rafraîchir" : "Refresh"}
                            </button>
                          </>
                        ) : (
                          <>
                            {/* Vincent B969 (Eric 2026-02-14 SPEC STRICK PROD) — Message
                                actionnable : le bouton « Refaire la fusion » est
                                maintenant visible ici en Line 2. On indique quoi faire
                                selon l'etat (fireflies importe ou non). */}
                            <ArrowClockwise size={36} className="mx-auto mb-3 opacity-30" />
                            <p className="text-sm font-medium text-zinc-700">
                              {lang === "fr" ? "Aucune transcription fusionnée pour cette vidéo" : "No merged transcript for this video"}
                            </p>
                            <p className="mt-2 text-xs text-zinc-500 max-w-md mx-auto">
                              {lang === "fr"
                                ? (allTranscripts?.summary?.has_whisper && allTranscripts?.summary?.has_fireflies
                                    ? "Clique sur le bouton « 🔀 Générer la fusion » dans la barre d'outils ci-dessus pour combiner Whisper + Fireflies en une transcription canonique corrigée."
                                    : "Importe d'abord une transcription Fireflies (onglet Fireflies) puis reviens ici pour générer la fusion via le bouton « 🔀 Générer la fusion » ci-dessus.")
                                : (allTranscripts?.summary?.has_whisper && allTranscripts?.summary?.has_fireflies
                                    ? "Click « 🔀 Generate fusion » in the toolbar above to combine Whisper + Fireflies into a canonical corrected transcript."
                                    : "First import a Fireflies transcript in the Fireflies tab, then come back here to generate the merge via the « 🔀 Generate fusion » button.")}
                            </p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div>
                        {/* Vincent B887 (Eric 2026-02-14 SPEC STRICK PROD) — Bannière de
                            couverture : si `fusion_diagnostic.content_coverage_alert === true`,
                            on alerte que la fusion a perdu plus de 10 % de contenu par
                            rapport à la source primaire (voir B885 pour le calcul). Un
                            bouton « Revoir » scrolle vers la fin du merged pour comparer
                            visuellement l'écart de durée. */}
                        {allTranscripts.merged.fusion_diagnostic?.content_coverage_alert && (() => {
                          const d = allTranscripts.merged.fusion_diagnostic;
                          const ratioPct = d.merged_coverage_ratio != null
                            ? Math.round(d.merged_coverage_ratio * 100)
                            : null;
                          const merged = d.merged_last_end_sec || 0;
                          const src = d.source_last_end_sec || 0;
                          const gapMin = Math.max(0, Math.round((src - merged) / 60 * 10) / 10);
                          const fmt = (s) => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,"0")}`;
                          return (
                            <div
                              className="mx-3 mt-3 mb-2 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900"
                              data-testid="merged-coverage-alert-banner"
                              role="alert"
                            >
                              <span aria-hidden className="text-xl leading-none mt-0.5">⚠️</span>
                              <div className="flex-1 text-sm min-w-0">
                                <p className="font-semibold" data-testid="merged-coverage-alert-title">
                                  {lang === "fr"
                                    ? `Couverture partielle : ${ratioPct ?? "?"} % de la source dans la fusion`
                                    : `Partial coverage: ${ratioPct ?? "?"}% of source in merge`}
                                </p>
                                <p className="mt-1 text-xs opacity-90" data-testid="merged-coverage-alert-detail">
                                  {lang === "fr"
                                    ? `Source primaire : ${fmt(src)} · Merged : ${fmt(merged)} · Manque ≈ ${gapMin} min. ${d.skipped_empty_source_segments ? `${d.skipped_empty_source_segments} segment(s) source vide(s) ignoré(s). ` : ""}Vérifiez que le fichier Fireflies importé couvre bien toute la durée de la vidéo.`
                                    : `Primary source: ${fmt(src)} · Merged: ${fmt(merged)} · Missing ≈ ${gapMin} min. ${d.skipped_empty_source_segments ? `${d.skipped_empty_source_segments} empty source segment(s) skipped. ` : ""}Check that the imported Fireflies file covers the full video duration.`}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={reviewCoverageBoundary}
                                className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-md border border-amber-700 bg-white hover:bg-amber-100"
                                data-testid="merged-coverage-alert-review-btn"
                              >
                                {lang === "fr" ? "Revoir" : "Review"}
                              </button>
                            </div>
                          );
                        })()}
                        <div className="px-3 py-2 bg-teal-50 border-b border-teal-100 text-[11px] text-teal-700 flex items-center gap-2">
                          <CheckCircle size={12} weight="fill" />
                          <span>
                            {(allTranscripts.merged.corrections_count || 0) === 0 ? (
                              <strong>{lang === "fr" ? "Fusion terminée · aucune correction nécessaire" : "Merge complete · no corrections needed"}</strong>
                            ) : (
                              <>
                                <strong>{allTranscripts.merged.corrections_count || 0}</strong> {lang === "fr" ? "corrections appliquées ·" : "corrections applied ·"} <strong>{allTranscripts.merged.modified_segments_count || 0}</strong> {lang === "fr" ? "segments modifiés" : "segments modified"}
                              </>
                            )}
                          </span>
                          <span className="ml-auto mr-5 text-[10px] text-teal-600/80">
                            {correctionBannersOpen && canEdit && (<>
                            <span className="text-zinc-700">texte normal</span> = commun ·{" "}
                            <span className="bg-rose-100 text-rose-900 font-semibold px-1 rounded" data-testid="legend-proposed">mot</span> = proposé ·{" "}
                            <span className="bg-blue-100 text-blue-800 font-semibold px-1 rounded" data-testid="legend-accepted">mot</span> = accepté
                            </>)}
                          </span>
                          {/* Tâche 1274 (Eric 2026-06-29) — Trois bandeaux
                              d'information empilés mangeaient la hauteur de la
                              page. Ce triangle les replie ; leur contenu reste
                              lisible en entier dans « Diagnostic orthographe ». */}
                          <button
                            type="button"
                            onClick={() => setCorrectionBannersOpen((v) => !v)}
                            className="flex-shrink-0 text-[11px] text-teal-700 hover:text-teal-900 px-1 leading-none"
                            data-testid="correction-banners-toggle"
                            data-open={correctionBannersOpen ? "1" : "0"}
                            title={correctionBannersOpen
                              ? (lang === "fr" ? "Replier les informations sur les corrections" : "Collapse correction info")
                              : (lang === "fr" ? "Afficher les informations sur les corrections" : "Show correction info")}
                            aria-expanded={correctionBannersOpen}
                          >
                            {correctionBannersOpen ? "▾" : "▸"}
                          </button>
                        </div>
                        {correctionBannersOpen && (<>
                        {/* Vincent B892 (Eric 2026-02-14 SPEC STRICK PROD) — Badge
                            couverture PERMANENT (contrairement à la bannière B887
                            qui n'apparaît qu'en cas d'alerte). Vincent voit d'un
                            coup d'œil la santé de la fusion, même quand tout va
                            bien. Palette progressive :
                              ratio ≥ 95 %  → vert (santé optimale)
                              ratio 90-95 % → zinc (limite basse acceptable)
                              ratio < 90 %  → ambre (aligné avec le seuil d'alerte
                                              B885 `content_coverage_alert`)
                              ratio inconnu → zinc muet (pas de diagnostic dispo) */}
                        {(() => {
                          const d = allTranscripts.merged.fusion_diagnostic || {};
                          // Bogue 891 refait — Repli sur la couverture calculée
                          // côté client : sans cela, le badge (et donc le bouton
                          // « Revoir ») restait invisible sur toutes les fusions
                          // sans `fusion_diagnostic`.
                          const srcSec = d.source_covered_duration_sec ?? (coverageInfo.srcEnd || null);
                          const mergedSec = d.merged_covered_duration_sec ?? (coverageInfo.mergedEnd || null);
                          const ratio = d.merged_coverage_ratio ?? coverageInfo.ratio;
                          // Bogue 891 refait — On n'abandonne plus faute de
                          // chiffres : tant qu'il y a une fusion à revoir, la
                          // ligne (et son bouton « Revoir la fin ») s'affiche,
                          // avec la mention « couverture inconnue ».
                          const hasNumbers = srcSec != null && mergedSec != null && srcSec > 0;
                          if (!mergedSegments.length) return null;
                          const fmtMin = (s) => {
                            const total = Math.round(s / 60 * 10) / 10;
                            return `${total} min`;
                          };
                          const pct = ratio != null ? Math.round(ratio * 100) : null;
                          let palette, testidTone;
                          if (ratio == null) {
                            palette = "bg-zinc-50 border-zinc-200 text-zinc-600";
                            testidTone = "unknown";
                          } else if (ratio >= 0.95) {
                            palette = "bg-emerald-50 border-emerald-200 text-emerald-800";
                            testidTone = "ok";
                          } else if (ratio >= 0.9) {
                            palette = "bg-zinc-50 border-zinc-200 text-zinc-700";
                            testidTone = "low";
                          } else {
                            palette = "bg-amber-50 border-amber-300 text-amber-900";
                            testidTone = "alert";
                          }
                          return (
                            <div
                              className={`px-3 py-2 border-b flex items-center gap-2 text-[11px] ${palette}`}
                              data-testid="merged-coverage-badge"
                              data-coverage-tone={testidTone}
                              data-coverage-ratio={ratio != null ? ratio.toFixed(3) : ""}
                              title={
                                lang === "fr"
                                  ? "Durée couverte par la fusion vs durée totale des segments source non vides"
                                  : "Duration covered by the merge vs total non-empty source segments duration"
                              }
                            >
                              <span aria-hidden>📏</span>
                              <span data-testid="merged-coverage-badge-text">
                                {hasNumbers ? (
                                  <>
                                    <strong data-testid="merged-coverage-badge-merged">{fmtMin(mergedSec)}</strong>
                                    {" / "}
                                    <strong data-testid="merged-coverage-badge-source">{fmtMin(srcSec)}</strong>
                                    {lang === "fr" ? " couvertes" : " covered"}
                                    {pct != null && (
                                      <> · <span data-testid="merged-coverage-badge-pct">{pct} %</span></>
                                    )}
                                  </>
                                ) : (
                                  lang === "fr" ? "Couverture inconnue (aucun horodatage exploitable)" : "Unknown coverage (no usable timecodes)"
                                )}
                              </span>
                              {/* Tâche 1260 — Sans cette mention, un pourcentage
                                  supérieur à 100 % passait pour un bogue : c'est
                                  au contraire du contenu récupéré de Fireflies
                                  que Whisper n'avait jamais transcrit. */}
                              {d.fireflies_only_injected_count > 0 && (
                                <span
                                  className="text-[10px] font-semibold text-violet-800"
                                  data-testid="merged-coverage-badge-injected"
                                  title={lang === "fr"
                                    ? "Passages entendus par Fireflies seul, injectés dans la fusion au lieu d'être perdus."
                                    : "Passages heard by Fireflies only, injected instead of being lost."}
                                >
                                  {lang === "fr"
                                    ? `+ ${d.fireflies_only_injected_sec} s récupérées de Fireflies (${d.fireflies_only_injected_count} passage(s))`
                                    : `+ ${d.fireflies_only_injected_sec}s recovered from Fireflies (${d.fireflies_only_injected_count})`}
                                </span>
                              )}
                              {d.skipped_empty_source_segments > 0 && (
                                <span
                                  className="ml-auto text-[10px] opacity-80"
                                  data-testid="merged-coverage-badge-skipped"
                                >
                                  {lang === "fr"
                                    ? `${d.skipped_empty_source_segments} segment(s) source vide(s) ignoré(s)`
                                    : `${d.skipped_empty_source_segments} empty source segment(s) skipped`}
                                </span>
                              )}
                              {coverageInfo.estimated && hasNumbers && (
                                <span className="text-[10px] opacity-70" data-testid="merged-coverage-badge-estimated">
                                  {lang === "fr" ? "(estimée)" : "(estimated)"}
                                </span>
                              )}
                              {/* Bogue 891 refait (Vincent 2026-06-25) — Le
                                  bouton « Revoir » est ici, sur le badge
                                  PERMANENT : il était invisible tant qu'il ne
                                  vivait que dans la bannière d'alerte. */}
                              <button
                                type="button"
                                onClick={reviewCoverageBoundary}
                                className="ml-auto flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-md border border-current bg-white/70 hover:bg-white"
                                data-testid="merged-coverage-review-btn"
                                title={lang === "fr"
                                  ? (coverageInfo.gapSec > 30
                                      ? `Surligner en jaune les derniers segments fusionnés (≈ ${Math.round(coverageInfo.gapSec / 60 * 10) / 10} min manquantes après)`
                                      : "Surligner en jaune la fin de la fusion pour vérifier la frontière")
                                  : "Highlight the last merged segments in yellow"}
                              >
                                {lang === "fr" ? "Revoir la fin" : "Review the end"}
                              </button>
                            </div>
                          );
                        })()}
                        {/* Eric / Vincent (2026-05-13) — Barre d'auto-accept pour les corrections
                            MINEURES (orthographe, accents, ponctuation, casse). Affichée uniquement
                            si la fusion contient au moins une correction mineure. Le compteur
                            « X / Y mineures acceptées » se met à jour après chaque action. */}
                        {(() => {
                          // Vincent #381 (Eric 2026-06-29 SPEC STRICK) — Apprenant
                          // (non-éditeur) ne voit AUCUN bouton "Tout accepter" /
                          // "Tout retirer". On masque toute la barre.
                          if (!canEdit) return null;
                          const allCorrs = (allTranscripts.merged.segments || []).flatMap(s => (s.corrections || []).map(c => ({ ...c, _segId: s.id })));
                          const minors = allCorrs.filter(c => c.category === "minor");
                          if (minors.length === 0) return null;
                          const acceptedMinors = minors.filter(c => c.accepted && !c.rejected).length;
                          const allAccepted = acceptedMinors === minors.length;
                          return (
                            <div className="px-3 py-2 bg-blue-50/60 border-b border-blue-100 flex items-center gap-3 text-[11px]" data-testid="bulk-minor-bar">
                              <span className="font-medium text-blue-900">
                                ✨ {lang === "fr" ? "Corrections mineures (orthographe / accents / ponctuation) :" : "Minor corrections:"}
                              </span>
                              <span className="text-blue-800" data-testid="bulk-minor-count">
                                <strong>{acceptedMinors}</strong> / {minors.length} {lang === "fr" ? "acceptée(s)" : "accepted"}
                              </span>
                              <span className="ml-auto flex items-center gap-2">
                                <button
                                  type="button"
                                  disabled={bulkMinorBusy || allAccepted}
                                  onClick={async () => {
                                    setBulkMinorBusy(true);
                                    try {
                                      const { data } = await axios.post(`${api}/api/videos/${videoId}/bulk-accept-minor-corrections`, {}, { withCredentials: true });
                                      toast.success(lang === "fr"
                                        ? `${data.accepted_count} correction(s) mineure(s) acceptée(s)`
                                        : `${data.accepted_count} minor correction(s) accepted`);
                                      await loadAllTranscripts();
                                    } catch {
                                      toast.error(lang === "fr" ? "Échec de l'acceptation en lot" : "Bulk accept failed");
                                    }
                                    setBulkMinorBusy(false);
                                  }}
                                  className="px-2.5 py-1 rounded border border-blue-400 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                                  data-testid="bulk-accept-minor-btn"
                                >
                                  ✓ {lang === "fr" ? "Tout accepter" : "Accept all"}
                                </button>
                                <button
                                  type="button"
                                  disabled={bulkMinorBusy || acceptedMinors === 0}
                                  onClick={async () => {
                                    setBulkMinorBusy(true);
                                    try {
                                      const { data } = await axios.post(`${api}/api/videos/${videoId}/bulk-unaccept-minor-corrections`, {}, { withCredentials: true });
                                      toast.success(lang === "fr"
                                        ? `${data.unaccepted_count} acceptation(s) retirée(s)`
                                        : `${data.unaccepted_count} acceptance(s) cleared`);
                                      await loadAllTranscripts();
                                    } catch {
                                      toast.error(lang === "fr" ? "Échec du rejet en lot" : "Bulk unaccept failed");
                                    }
                                    setBulkMinorBusy(false);
                                  }}
                                  className="px-2.5 py-1 rounded border border-blue-300 bg-white text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                                  data-testid="bulk-unaccept-minor-btn"
                                >
                                  ↶ {lang === "fr" ? "Tout retirer" : "Unaccept all"}
                                </button>
                              </span>
                            </div>
                          );
                        })()}
                        </>)}
                        {/* Tâche 1273 (Eric 2026-06-29) — Le bandeau dépliable
                            qui répétait la méthode de fusion a été RETIRÉ :
                            son contenu vit désormais dans la fenêtre du bouton
                            « Diagnostic fusion », et la page gagne la place
                            qu'il occupait. */}
                        {/* Vincent bug #111 (2026-05-08) — Barre d'outils transcription fusionnée :
                            Ordre fixe (gauche → droite) : 1.Refaire fusion · 2.Orthographe · 3.Valider URLs.
                            Icône au-dessus, texte en dessous.
                            Vincent #355 (Eric 2026-06-22 SPEC STRICK 3e passe) — Toute la barre
                            est désormais GATED canEdit : les non-éditeurs ne voient
                            pas non plus "Refaire la fusion", "Orthographe",
                            "Valider les URLs".
                            Vincent B618 (Eric 2026-07-29 SPEC STRICK) — Bouton
                            "Ponctuation" RETIRÉ : sa fonction est déjà couverte
                            par le bouton "Orthographe" (orthographe + accents +
                            ponctuation + casse en un seul pass GPT-5.2). La
                            grille passe de grid-cols-4 à grid-cols-3. */}
                        {/* Vincent B935 (Eric 2026-02-14 SPEC STRICK PROD) —
                            La toolbar « grande » de 3 boutons (Refaire la fusion,
                            Correction orthographe, Valider URLs) est retirée : ces
                            actions sont désormais accessibles UNIQUEMENT via la
                            barre de boutons compacte au-dessus de la transcription
                            (Ligne 2 de TranscriptPanel). On garde le code sous
                            `false &&` pour référence, mais l'UI n'affiche plus
                            ces gros doublons. */}
                        {false && canEdit && (
                        <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 grid grid-cols-3 gap-1.5" data-testid="merged-toolbar">
                          <button
                            onClick={async () => {
                              if (crossValidating || !fusionAvailable) return;
                              // Vincent B640 (Eric 2026-07-31 SPEC STRICK PROD)
                              // — Guard supplémentaire : les 2 transcriptions
                              // DOIVENT exister pour pouvoir refaire une fusion.
                              const _hasBoth = !!allTranscripts?.summary?.has_whisper && !!allTranscripts?.summary?.has_fireflies;
                              if (!_hasBoth) return;
                              const hasMerged = !!allTranscripts?.summary?.has_merged;
                              const ok = await confirmDialog(hasMerged
                                ? (lang === "fr" ? "Refaire la fusion avec la transcription Fireflies existante ? La version fusionnée actuelle sera remplacée." : "Re-run merge with existing Fireflies transcript? Current merged version will be replaced.")
                                : (lang === "fr" ? "Générer la fusion à partir de Whisper + Fireflies ? Cela crée la transcription canonique utilisée par Q&R / Scripts / Procédures." : "Generate the merged transcript from Whisper + Fireflies?"),
                                { tone: "warning" }
                              );
                              if (!ok) return;
                              setCrossValidating(true);
                              setCrossValidateResult(null);
                              try {
                                const { data } = await axios.post(`${api}/api/videos/${videoId}/re-merge`, {}, { withCredentials: true });
                                setCrossValidateResult(data);
                                await loadAllTranscripts();
                                pollMergedTranscript();
                              } catch (err) {
                                console.error(err);
                                toast.error(lang === "fr"
                                  ? `Erreur : ${err.response?.data?.detail || err.message}`
                                  : `Error: ${err.response?.data?.detail || err.message}`);
                                setCrossValidating(false);
                              }
                            }}
                            /* Vincent B640 (Eric 2026-07-31 SPEC STRICK PROD)
                               — Le bouton "Refaire la fusion" doit être
                               inaccessible tant qu'une seule des 2 transcriptions
                               existe. Il redevient actif dès qu'on a Whisper ET
                               Fireflies. */
                            disabled={
                              crossValidating
                              || !fusionAvailable
                              || !allTranscripts?.summary?.has_whisper
                              || !allTranscripts?.summary?.has_fireflies
                            }
                            className="flex flex-col items-center justify-center gap-1 px-2 py-2 rounded border border-teal-300 bg-white text-teal-700 hover:bg-teal-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            data-testid="re-merge-btn"
                            title={
                              !allTranscripts?.summary?.has_whisper
                                ? (lang === "fr" ? "Transcription Whisper requise" : "Whisper transcript required")
                                : !allTranscripts?.summary?.has_fireflies
                                  ? (lang === "fr" ? "Transcription Fireflies requise pour fusionner" : "Fireflies transcript required to merge")
                                  : fusionAvailable
                                    ? (lang === "fr" ? "Relancer la fusion sans re-uploader le fichier Fireflies" : "Re-run the merge without re-uploading the Fireflies file")
                                    : fusionBlockedReason
                            }
                          >
                            <ArrowClockwise size={16} />
                            <span className="text-[10px] leading-tight font-medium">
                              {allTranscripts?.summary?.has_merged
                                ? (lang === "fr" ? "Refaire la fusion" : "Re-run merge")
                                : (lang === "fr" ? "Générer la fusion" : "Generate merge")}
                            </span>
                          </button>
                          <button
                            onClick={async () => {
                              if (crossValidating) return;
                              const ok = await confirmDialog(lang === "fr" ? "Lancer une correction linguistique automatique (orthographe, accents, ponctuation) ? Les propositions seront appliquées directement — utilisez le ✗ pour rejeter une par une." : "Run automatic linguistic correction? Changes are applied live — use ✗ to reject individual ones.", { tone: "warning" });
                              if (!ok) return;
                              setCrossValidating(true);
                              try {
                                const { data: proposed } = await axios.post(`${api}/api/videos/${videoId}/propose-linguistic-corrections`, {}, { withCredentials: true });
                                // Vincent B880 (Eric 2026-02-14 SPEC STRICK PROD) —
                                // Distingue « texte réellement propre » (0 corrections
                                // ET 0 lot en échec) de « LLM partiellement KO » (0
                                // corrections MAIS N lots en échec) — Vincent ne peut
                                // plus confondre un no-op silencieux avec un vrai
                                // "rien à corriger".
                                const totalBatches = proposed.total_batches ?? 0;
                                const failed = proposed.failed_batches ?? 0;
                                if ((proposed.corrections || []).length === 0) {
                                  if (failed > 0) {
                                    // Toast warning avec le premier message d'erreur.
                                    const firstErr = (proposed.errors && proposed.errors[0]) || "";
                                    toast.error(
                                      lang === "fr"
                                        ? `Correction linguistique partielle : ${failed}/${totalBatches} lot(s) GPT-5.2 en échec. ${firstErr}`
                                        : `Linguistic correction partial: ${failed}/${totalBatches} GPT-5.2 batch(es) failed. ${firstErr}`
                                    );
                                  } else {
                                    toast.success(lang === "fr" ? "Aucune correction linguistique nécessaire." : "No linguistic correction needed.");
                                  }
                                  setCrossValidating(false);
                                  return;
                                }
                                await axios.post(`${api}/api/videos/${videoId}/apply-linguistic-corrections`, { corrections: proposed.corrections }, { withCredentials: true });
                                // B880 — Si des lots ont échoué, on prévient qu'il en manque potentiellement.
                                if (failed > 0) {
                                  toast.warning(
                                    lang === "fr"
                                      ? `${proposed.corrections.length} corrections appliquées mais ${failed}/${totalBatches} lot(s) GPT-5.2 ont échoué — relance pour compléter.`
                                      : `${proposed.corrections.length} corrections applied but ${failed}/${totalBatches} GPT-5.2 batch(es) failed — rerun to complete.`
                                  );
                                } else {
                                  toast.success(lang === "fr" ? `${proposed.corrections.length} corrections appliquées` : `${proposed.corrections.length} corrections applied`);
                                }
                                await Promise.all([loadAllTranscripts(), loadVideoData()]);
                              } catch (err) {
                                toast.error(lang === "fr" ? `Erreur : ${err.response?.data?.detail || err.message}` : `Error: ${err.response?.data?.detail || err.message}`);
                              }
                              setCrossValidating(false);
                            }}
                            disabled={crossValidating}
                            className="flex flex-col items-center justify-center gap-1 px-2 py-2 rounded border border-blue-300 bg-white text-blue-700 hover:bg-blue-50 disabled:opacity-40 transition-colors"
                            data-testid="linguistic-correction-btn"
                            title={lang === "fr" ? "Corriger automatiquement orthographe, accents et ponctuation (sans modifier le sens)." : "Auto-correct orthography, accents and punctuation."}
                          >
                            <span className="text-base">✨</span>
                            <span className="text-[10px] leading-tight font-medium">{lang === "fr" ? "Correction orthographe" : "Spelling fix"}</span>
                          </button>
                          {/* Vincent B618 (Eric 2026-07-29 SPEC STRICK) —
                              Bouton « Ponctuation » RETIRÉ. La correction
                              de ponctuation est déjà exécutée par le bouton
                              « Orthographe » (voir server côté GPT-5.2 :
                              orthographe + accents + ponctuation + casse
                              en un seul pass). Le doublon confondait
                              Vincent et générait des passes redondantes.
                              La logique backend `propose-punctuation` est
                              conservée pour rétrocompat mais plus déclenchable
                              depuis l'UI. */}
                          <button
                            onClick={async () => {
                              if (urlValidating) return;
                              setUrlValidating(true);
                              setUrlProposals(null);
                              try {
                                const { data } = await axios.post(`${api}/api/videos/${videoId}/validate-urls`, {}, { withCredentials: true });
                                setUrlProposals(data);
                              } catch (err) {
                                toast.error(lang === "fr" ? `Erreur : ${err.response?.data?.detail || err.message}` : `Error: ${err.response?.data?.detail || err.message}`);
                              }
                              setUrlValidating(false);
                            }}
                            disabled={urlValidating}
                            className="flex flex-col items-center justify-center gap-1 px-2 py-2 rounded border border-amber-300 bg-white text-amber-700 hover:bg-amber-50 disabled:opacity-40 transition-colors"
                            data-testid="validate-urls-btn"
                            title={lang === "fr" ? "Détecter les URLs prononcées dans la vidéo et vérifier qu'elles existent." : "Detect spoken URLs and verify they exist."}
                          >
                            <span className="text-base">🔗</span>
                            <span className="text-[10px] leading-tight font-medium">{urlValidating ? (lang === "fr" ? "Vérification..." : "Checking...") : (lang === "fr" ? "Valider URLs" : "Validate URLs")}</span>
                          </button>
                        </div>
                        )}
                        {/* URL validation results panel (Vincent 2026-05-01) */}
                        {urlProposals && (urlProposals.proposals || []).length === 0 && (
                          <div className="mx-4 my-3 p-3 bg-teal-50 border border-teal-200 rounded-lg text-[11px] flex items-center justify-between" data-testid="url-proposals-empty-panel">
                            <p className="font-semibold text-teal-900">
                              ✅ {lang === "fr" ? "Aucune URL détectée dans la transcription." : "No URL detected in the transcript."}
                            </p>
                            <button
                              onClick={() => setUrlProposals(null)}
                              className="text-teal-700 hover:bg-teal-100 rounded px-2 py-0.5 text-[10px]"
                              data-testid="url-proposals-empty-close"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                        {urlProposals && (urlProposals.proposals || []).length > 0 && (
                          <div className="mx-4 my-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-[11px]" data-testid="url-proposals-panel">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-semibold text-amber-900">
                                🔗 {lang === "fr" ? `${urlProposals.detected} URL(s) détectée(s)` : `${urlProposals.detected} URL(s) detected`}
                              </p>
                              <button
                                onClick={() => setUrlProposals(null)}
                                className="text-amber-700 hover:bg-amber-100 rounded px-2 py-0.5 text-[10px]"
                                data-testid="url-proposals-close"
                              >
                                ✕
                              </button>
                            </div>
                            <ul className="space-y-2">
                              {urlProposals.proposals.map((p, i) => {
                                const isOk = p.status === "ok";
                                const isAccentedFixable = p.status === "accented_correctable";
                                // Treat the accented-but-correctable case as a "soft warning"
                                // (amber, not red): the host RESOLVES, just needs accents stripped.
                                const cardBg = isOk ? "bg-teal-50 border-teal-200"
                                              : isAccentedFixable ? "bg-amber-50 border-amber-300"
                                              : "bg-white border-red-300";
                                const badgeBg = isOk ? "bg-teal-200 text-teal-900"
                                              : isAccentedFixable ? "bg-amber-200 text-amber-900"
                                              : "bg-red-200 text-red-900";
                                const badgeLabel = isOk ? (lang === "fr" ? "existe" : "exists")
                                                : isAccentedFixable ? (lang === "fr" ? "accents a retirer" : "accents to strip")
                                                : (lang === "fr" ? "introuvable" : "not found");
                                return (
                                  <li key={i} className={`p-2 rounded border ${cardBg}`} data-testid={`url-proposal-${i}`}>
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <span className="font-mono text-zinc-700 break-all">{p.raw}</span>
                                        <span className={`ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${badgeBg}`}>
                                          {badgeLabel}
                                        </span>
                                      </div>
                                    </div>
                                    {!isOk && (() => {
                                      // Vincent bug #18 / #95 (2026-05-08) — propose un menu déroulant
                                      // unifié (DNS + IA + Appris) au lieu de buttons éparpillés. L'utilisateur
                                      // sélectionne UNE option et clique « Valider » (appliquer la
                                      // correction) ou « Ouvrir » (tester l'URL dans un nouvel onglet).
                                      // Suggestion Eric (2026-05-08) — Si une correction a déjà été
                                      // validée par cette org, elle est promue en tête avec le badge 🧠.
                                      const learnedCorrected = p.learned_correction?.corrected;
                                      const dnsOpts = (p.suggestions || []).map(s => ({
                                        value: s,
                                        source: s === learnedCorrected ? "learned" : "dns",
                                        votes: s === learnedCorrected ? (p.learned_correction?.votes || 1) : null,
                                      }));
                                      const aiOpts = (p.ai_suggestions || []).map(s => ({ value: s, source: "ai" }));
                                      const allOpts = [...dnsOpts, ...aiOpts];
                                      if (allOpts.length === 0) return null;
                                      const dropdownKey = `url-${i}`;
                                      const selectedOpt = (urlDropdownSelections && urlDropdownSelections[dropdownKey]) || allOpts[0].value;
                                      const applyKey = `${p.raw}::${selectedOpt}`;
                                      return (
                                        <div className="mt-1.5 pl-2 border-l-2 border-amber-300" data-testid={`url-dropdown-${i}`}>
                                          <p className="text-zinc-600 text-[10px] mb-1">
                                            {isAccentedFixable
                                              ? (lang === "fr" ? "Correction proposée :" : "Proposed fix:")
                                              : (lang === "fr" ? `${allOpts.length} option(s) à tester (DNS + IA) :` : `${allOpts.length} option(s) (DNS + AI):`)}
                                          </p>
                                          <div className="flex items-center gap-2">
                                            <select
                                              value={selectedOpt}
                                              onChange={(e) => setUrlDropdownSelections(prev => ({ ...(prev || {}), [dropdownKey]: e.target.value }))}
                                              className="flex-1 min-w-0 text-[11px] font-mono px-2 py-1 border border-zinc-300 rounded bg-white"
                                              data-testid={`url-dropdown-select-${i}`}
                                            >
                                              {allOpts.map((opt, oi) => (
                                                <option key={oi} value={opt.value}>
                                                  {`${opt.source === "learned"
                                                    ? `🧠 Appris (${opt.votes}×) · `
                                                    : opt.source === "ai"
                                                      ? "🤖 IA · "
                                                      : "🌐 DNS · "}${opt.value}`}
                                                </option>
                                              ))}
                                            </select>
                                            <button
                                              onClick={async () => {
                                                if (urlApplying === applyKey) return;
                                                setUrlApplying(applyKey);
                                                try {
                                                  await axios.post(`${api}/api/videos/${videoId}/apply-linguistic-corrections`, {
                                                    corrections: [{ segment_id: null, original: p.raw, correction: selectedOpt, reason: "url_correction" }]
                                                  }, { withCredentials: true });
                                                  toast.success(lang === "fr" ? `URL corrigée : ${selectedOpt}` : `URL fixed: ${selectedOpt}`);
                                                  setUrlProposals((prev) => ({
                                                    ...prev,
                                                    proposals: prev.proposals.filter((_, idx) => idx !== i),
                                                  }));
                                                  await Promise.all([loadAllTranscripts(), loadVideoData()]);
                                                } catch (err) {
                                                  toast.error(lang === "fr" ? "Application échouée" : "Apply failed");
                                                }
                                                setUrlApplying(null);
                                              }}
                                              disabled={urlApplying === applyKey}
                                              className="text-[10px] px-3 py-1 rounded bg-[#002FA7] hover:bg-[#001F7A] text-white font-medium disabled:opacity-50"
                                              data-testid={`url-dropdown-apply-${i}`}
                                            >
                                              {urlApplying === applyKey
                                                ? (lang === "fr" ? "..." : "...")
                                                : (lang === "fr" ? "✓ Valider" : "✓ Validate")}
                                            </button>
                                            <a
                                              href={`https://${selectedOpt}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-[10px] px-2 py-1 rounded border border-zinc-300 text-zinc-600 hover:bg-zinc-50"
                                              title={lang === "fr" ? "Ouvrir l'URL dans un nouvel onglet pour vérifier le site" : "Open URL in new tab"}
                                              data-testid={`url-dropdown-test-${i}`}
                                            >
                                              ↗ {lang === "fr" ? "Ouvrir" : "Open"}
                                            </a>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                    {/* Legacy quick-buttons retained ONLY for the special case "accented_correctable" → 1-click obvious fix */}
                                    {!isOk && false && p.suggestions && p.suggestions.length > 0 && (
                                      <div className="mt-1.5 pl-2 border-l-2 border-amber-300">
                                        <p className="text-zinc-500 text-[10px] mb-1">
                                          {isAccentedFixable
                                            ? (lang === "fr" ? "Correction proposee :" : "Proposed fix:")
                                            : (lang === "fr" ? "Variantes qui resolvent au DNS :" : "DNS-resolving variants:")}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                          {p.suggestions.map((s, si) => (
                                            <button
                                              key={si}
                                              onClick={async () => {
                                                const key = `${p.raw}::${s}`;
                                                if (urlApplying === key) return;
                                                setUrlApplying(key);
                                                try {
                                                  await axios.post(`${api}/api/videos/${videoId}/apply-linguistic-corrections`, {
                                                    corrections: [{ segment_id: null, original: p.raw, correction: s, reason: "url_correction" }]
                                                  }, { withCredentials: true });
                                                  toast.success(lang === "fr" ? `URL corrigee : ${s}` : `URL fixed: ${s}`);
                                                  setUrlProposals((prev) => ({
                                                    ...prev,
                                                    proposals: prev.proposals.filter((_, idx) => idx !== i),
                                                  }));
                                                  // Vincent 2026-05-05 — refresh tags après URL correction
                                                  await Promise.all([loadAllTranscripts(), loadVideoData()]);
                                                } catch (err) {
                                                  toast.error(lang === "fr" ? "Application echouee" : "Apply failed");
                                                }
                                                setUrlApplying(null);
                                              }}
                                              disabled={urlApplying === `${p.raw}::${s}`}
                                              className={`px-2 py-0.5 rounded font-mono text-[10px] disabled:opacity-50 ${si === 0 && isAccentedFixable ? "bg-teal-600 hover:bg-teal-700 text-white ring-1 ring-emerald-700" : "bg-[#002FA7] hover:bg-[#001F7A] text-white"}`}
                                              data-testid={`url-suggestion-${i}-${si}`}
                                            >
                                              {si === 0 && isAccentedFixable ? "✓ " : "→ "}{s}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {!isOk && p.ai_suggestions && p.ai_suggestions.length > 0 && (
                                      <div className="mt-1.5 pl-2 border-l-2 border-purple-300">
                                        <p className="text-purple-700 text-[10px] mb-1 font-semibold">
                                          🤖 {lang === "fr" ? "Suggestions IA basées sur le contexte :" : "AI suggestions from context:"}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                          {p.ai_suggestions.map((s, si) => (
                                            <button
                                              key={`ai-${si}`}
                                              onClick={async () => {
                                                const key = `${p.raw}::ai::${s}`;
                                                if (urlApplying === key) return;
                                                setUrlApplying(key);
                                                try {
                                                  await axios.post(`${api}/api/videos/${videoId}/apply-linguistic-corrections`, {
                                                    corrections: [{ segment_id: null, original: p.raw, correction: s, reason: "url_correction" }]
                                                  }, { withCredentials: true });
                                                  toast.success(lang === "fr" ? `URL corrigée (IA) : ${s}` : `URL fixed (AI): ${s}`);
                                                  setUrlProposals((prev) => ({
                                                    ...prev,
                                                    proposals: prev.proposals.filter((_, idx) => idx !== i),
                                                  }));
                                                  await Promise.all([loadAllTranscripts(), loadVideoData()]);
                                                } catch (err) {
                                                  toast.error(lang === "fr" ? "Application échouée" : "Apply failed");
                                                }
                                                setUrlApplying(null);
                                              }}
                                              disabled={urlApplying === `${p.raw}::ai::${s}`}
                                              className="px-2 py-0.5 rounded font-mono text-[10px] disabled:opacity-50 bg-purple-600 hover:bg-purple-700 text-white"
                                              data-testid={`url-ai-suggestion-${i}-${si}`}
                                              title={lang === "fr" ? "Suggestion IA (DNS-validée)" : "AI suggestion (DNS-validated)"}
                                            >
                                              ✨ {s}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {!isOk && (!p.suggestions || p.suggestions.length === 0) && (!p.ai_suggestions || p.ai_suggestions.length === 0) && (
                                      <p className="mt-1 text-[10px] text-zinc-500 italic">
                                        {lang === "fr" ? "Aucune variante DNS-valide trouvée (DNS ni IA). Corrigez manuellement si nécessaire." : "No DNS-valid variant found (DNS or AI). Edit manually if needed."}
                                      </p>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                        <div className="px-4 py-3" data-testid="merged-segments-3col">
                          {/* Tâche 1175 (Eric 2026-06-25) — La légende des
                              pastilles de provenance (bogue 888) devient
                              CLIQUABLE : un filtre par provenance pour voir
                              d'un coup ce que Fireflies n'a pas couvert
                              (« Whisper seul ») et ce que Whisper n'a pas
                              couvert (« Fireflies seul »). */}
                          <div
                            className="flex flex-wrap items-center gap-2 pb-2 mb-1 border-b border-zinc-100 text-[10px] text-zinc-500"
                            data-testid="merged-source-legend"
                          >
                            <span className="font-semibold uppercase tracking-wider text-zinc-400">
                              {lang === "fr" ? "Provenance" : "Source"}
                            </span>
                            {[
                              ["all", lang === "fr" ? "Tout" : "All", segmentSourceCounts.all],
                              ["fused", lang === "fr" ? "Fusionnée" : "Fused", segmentSourceCounts.fused],
                              ["whisper", lang === "fr" ? "Whisper seul" : "Whisper only", segmentSourceCounts.whisper],
                              ["fireflies", lang === "fr" ? "Fireflies seul" : "Fireflies only", segmentSourceCounts.fireflies],
                            ].map(([key, label, count]) => (
                              <button
                                key={key}
                                type="button"
                                onClick={() => setSourceFilter(key)}
                                aria-pressed={sourceFilter === key}
                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition-colors ${
                                  sourceFilter === key
                                    ? "border-zinc-800 bg-zinc-900 text-white"
                                    : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
                                }`}
                                data-testid={`merged-source-legend-${key}`}
                                title={key === "whisper"
                                  ? (lang === "fr" ? "Passages que Fireflies n'a pas couverts" : "Passages Fireflies did not cover")
                                  : key === "fireflies"
                                    ? (lang === "fr" ? "Passages que Whisper n'a pas couverts" : "Passages Whisper did not cover")
                                    : undefined}
                              >
                                {key !== "all" && (
                                  <span className={`w-1.5 h-1.5 rounded-full ${SEGMENT_SOURCE_META[key].dot}`} aria-hidden="true" />
                                )}
                                {label}
                                <span className="tabular-nums opacity-70">({count})</span>
                              </button>
                            ))}
                          </div>
                          {sourceFilter !== "all" && segmentSourceCounts[sourceFilter] === 0 && (
                            <p
                              className="py-3 text-xs text-zinc-500 italic"
                              data-testid="merged-source-filter-empty"
                            >
                              {sourceFilter === "fireflies"
                                ? (lang === "fr"
                                    ? "Aucun passage couvert par Fireflies seul : tout ce que Fireflies a dit se retrouve dans la fusion."
                                    : "No Fireflies-only passage: everything Fireflies captured made it into the fusion.")
                                : sourceFilter === "whisper"
                                  ? (lang === "fr"
                                      ? "Aucun passage couvert par Whisper seul : Fireflies couvre toute la durée fusionnée."
                                      : "No Whisper-only passage: Fireflies covers the whole fused timeline.")
                                  : (lang === "fr" ? "Aucun segment pour ce filtre." : "No segment for this filter.")}
                            </p>
                          )}
                          {/* Vincent #54 + #100 (2026-05-12) — Fusionnée en 3 colonnes
                              IDENTIQUES à Whisper/Fireflies : [MM:SS] | Locuteur | Citation. */}
                          {(allTranscripts.merged.segments || []).map((seg, mi) => {
                            const speakerColor = speakerMap[seg.speaker] || "#71717a";
                            // Vincent B891 — Highlight temporaire quand le
                            // segment fait partie de la « frontière du contenu
                            // manquant » (voir bouton Revoir de la bannière B887).
                            const isCoverageHighlighted = coverageHighlightSegIds?.has(seg.id) || false;
                            // Bogue 888 — provenance de CE segment.
                            const segSource = resolveSegmentSource(seg);
                            const segSourceMeta = SEGMENT_SOURCE_META[segSource];
                            const segSourceLabel = lang === "fr" ? segSourceMeta.fr : segSourceMeta.en;
                            // Tâche 1175 — filtre par provenance. On renvoie
                            // null plutôt que de filtrer le tableau : les index
                            // `mi` (testids, focus segment B766) restent stables.
                            if (sourceFilter !== "all" && segSource !== sourceFilter) return null;
                            return (
                              <div key={seg.id || mi} data-seg-id={seg.id}
                                   data-temps={Number.isFinite(Number(seg.start)) ? Number(seg.start) : ""}>
                                <div
                                  className={`grid grid-cols-[70px_90px_1fr] gap-3 items-start py-1.5 cursor-pointer rounded transition-colors hover:bg-zinc-50 ${
                                    focusedIdx === mi ? "ring-2 ring-purple-400 bg-purple-50" : ""
                                  } ${
                                    isCoverageHighlighted ? "ring-2 ring-amber-400 bg-amber-100/70 animate-pulse" : ""
                                  }`}
                                  onClick={() => seekTo(seg.start)}
                                  data-testid={`merged-segment-${mi}`}
                                  data-focused={focusedIdx === mi ? "yes" : "no"}
                                  data-coverage-highlighted={isCoverageHighlighted ? "yes" : "no"}
                                >
                                  <span className="relative group/src inline-block">
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); seekTo(seg.start); }}
                                      className="text-[11px] font-mono text-left tabular-nums leading-relaxed text-zinc-400 hover:text-[#002FA7] inline-flex items-center gap-1"
                                      data-testid={`merged-timecode-${mi}`}
                                      data-source={segSource}
                                      aria-label={`${formatTimeBracket(seg.start)} — ${segSourceLabel}`}
                                      title={`${segSourceLabel}\n${lang === "fr" ? "Cliquez pour aller à ce timecode" : "Click to jump to this timecode"}`}
                                    >
                                      <span
                                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${segSourceMeta.dot}`}
                                        data-testid={`merged-timecode-source-dot-${mi}`}
                                        aria-hidden="true"
                                      />
                                      {formatTimeBracket(seg.start)}
                                    </button>
                                    {seg.injected_from === "fireflies" && (
                                      <span
                                        className="ml-1 align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-900 border border-violet-300"
                                        title={lang === "fr"
                                          ? "Passage entendu par Fireflies seul, injecté dans la fusion (tâche 1260) : Whisper ne l'avait pas transcrit."
                                          : "Passage heard by Fireflies only, injected into the fusion: Whisper had missed it."}
                                        data-testid={`merged-fireflies-injected-badge-${mi}`}
                                      >
                                        {lang === "fr" ? "ajout Fireflies" : "Fireflies add"}
                                      </span>
                                    )}
                                    {segSource === "manuel" && (
                                      <span
                                        className="ml-1 align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300"
                                        title={`${lang === "fr" ? "Ajout manuel de" : "Manual addition by"} ${
                                          seg.manual_fill?.by || "?"
                                        } — ${(seg.manual_fill?.at || "").slice(0, 16).replace("T", " ")}`}
                                        data-testid={`merged-manual-badge-${mi}`}
                                      >
                                        {lang === "fr" ? "ajout manuel" : "manual"}
                                      </span>
                                    )}
                                    {/* Bogue 888 — infobulle instantanée (le
                                        `title` natif n'apparaissait qu'après
                                        une seconde et passait inaperçu). */}
                                    <span
                                      role="tooltip"
                                      className="pointer-events-none absolute left-0 top-full z-50 mt-1 hidden w-max max-w-[220px] rounded-md bg-zinc-900 px-2 py-1 text-[10px] font-semibold leading-snug text-white shadow-lg group-hover/src:block"
                                      data-testid={`merged-timecode-source-${mi}`}
                                    >
                                      {segSourceLabel}
                                    </span>
                                  </span>
                                  {/* B776 (Eric 2026-02-17 SPEC STRICK PROD)
                                      — Speaker sur 2 lignes (prénom / nom)
                                      dans une colonne plus étroite (140 → 90 px)
                                      pour libérer de l'espace au texte. */}
                                  <div
                                    className="text-[11px] font-semibold leading-tight break-words flex items-start justify-between gap-1"
                                    style={{ color: speakerColor }}
                                    data-testid={`merged-speaker-${mi}`}
                                    title={seg.speaker || "Inconnu"}
                                  >
                                    <span className="min-w-0">
                                      {formatSpeakerLines(seg.speaker).map((line, li) => (
                                        <span key={li} className="block">{line}</span>
                                      ))}
                                    </span>
                                    {/* Tâche 1349 (Eric 2026-06-30) — Le crayon
                                        sort de la case de texte : posé dedans
                                        (tâche 1332), il poussait la première
                                        phrase de chaque tour vers la droite et
                                        cassait l'alignement de la colonne. Il se
                                        tient À DROITE du nom du locuteur, sur la
                                        même ligne, jamais dessous. */}
                                    {canEdit && seg.id && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingSegId(seg.id);
                                          setSegDraft(seg.merged_text || seg.text || "");
                                        }}
                                        className="shrink-0 w-5 h-5 rounded inline-flex items-center justify-center border border-zinc-200 text-zinc-400 hover:border-[#1e3a8a] hover:text-[#1e3a8a] hover:bg-blue-50 transition-colors"
                                        data-testid={`merged-edit-sentence-btn-${mi}`}
                                        aria-label={lang === "fr" ? "Corriger cette phrase" : "Edit this sentence"}
                                        title={lang === "fr"
                                          ? "Corriger cette phrase à la main. La version d'avant reste dans l'Historique."
                                          : "Edit this sentence manually. The previous version stays in History."}
                                      >
                                        <PencilSimple size={12} />
                                      </button>
                                    )}
                                  </div>
                                  {editingSegId === seg.id && seg.id ? (
                                    /* Tâche 1332 — La phrase devient un champ
                                       de saisie à la place du texte : on
                                       corrige là où on lit, sans quitter la
                                       ligne ni perdre le contexte. */
                                    <div
                                      className="space-y-1.5"
                                      onClick={(e) => e.stopPropagation()}
                                      data-testid={`merged-edit-sentence-form-${mi}`}
                                    >
                                      <textarea
                                        value={segDraft}
                                        onChange={(e) => setSegDraft(e.target.value)}
                                        rows={Math.min(8, Math.max(2, Math.ceil((segDraft.length || 1) / 90)))}
                                        autoFocus
                                        className="w-full text-sm leading-relaxed px-2 py-1.5 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                                        data-testid={`merged-edit-sentence-input-${mi}`}
                                      />
                                      <div className="flex items-center gap-1.5">
                                        <button
                                          type="button"
                                          onClick={() => saveSentence(seg)}
                                          disabled={segSaving || !segDraft.trim()}
                                          className="px-2.5 py-1 rounded-md bg-amber-600 text-white text-[11px] font-semibold hover:bg-amber-700 disabled:opacity-50"
                                          data-testid={`merged-edit-sentence-save-${mi}`}
                                        >
                                          {segSaving
                                            ? (lang === "fr" ? "Enregistrement…" : "Saving…")
                                            : (lang === "fr" ? "Enregistrer" : "Save")}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => { setEditingSegId(null); setSegDraft(""); }}
                                          disabled={segSaving}
                                          className="px-2.5 py-1 rounded-md border border-zinc-300 text-zinc-600 text-[11px] font-medium hover:bg-zinc-50 disabled:opacity-50"
                                          data-testid={`merged-edit-sentence-cancel-${mi}`}
                                        >
                                          {lang === "fr" ? "Annuler" : "Cancel"}
                                        </button>
                                        <span className="text-[10px] text-zinc-500">
                                          {lang === "fr"
                                            ? "La version d'avant reste dans l'Historique."
                                            : "The previous version stays in History."}
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                  <div className="min-w-0">
                                    {/* Tâche 1349 — Une phrase réécrite à la
                                        main s'affiche en bleu foncé : elle ne
                                        vient ni de la machine (noir) ni d'une
                                        correction du modèle (surlignée). */}
                                    <span
                                      className="text-sm leading-relaxed whitespace-pre-wrap text-zinc-700"
                                      data-testid={`merged-text-${mi}`}
                                      data-hand-edited={seg.manual_edit ? "true" : "false"}
                                    >
                                    {(() => {
                                      const text = seg.merged_text || "";
                                      // Tâche 1349 rev2 (Eric 2026-06-30) — SEULS
                                      // les mots changés à la main passent en bleu
                                      // foncé, et ceux qui ont disparu restent
                                      // visibles barrés en bleu clair. La première
                                      // version peignait la phrase entière : la
                                      // couleur ne disait plus où regarder.
                                      if (seg.manual_edit) {
                                        const avant = seg.manual_edit.original_text
                                          || seg.manual_edit.previous_text || "";
                                        return diffMots(avant, text).map((part, i) => {
                                          if (part.type === "egal") return <span key={i}>{part.texte}</span>;
                                          if (part.type === "supprime") {
                                            // Un long passage retiré, affiché en
                                            // entier, noyait la phrase corrigée :
                                            // on en montre le début et on dit
                                            // combien de signes ont sauté. Le
                                            // texte complet reste dans l'infobulle
                                            // et dans l'Historique.
                                            const long = part.texte.length > 120;
                                            return (
                                              <span
                                                key={i}
                                                className="line-through text-blue-400"
                                                data-testid={`merged-hand-removed-${mi}-${i}`}
                                                title={`${lang === "fr" ? "Retiré à la main" : "Removed by hand"} : ${part.texte}`}
                                              >
                                                {long ? `${part.texte.slice(0, 60).trimEnd()}…` : part.texte}
                                                {long && (
                                                  <span className="no-underline text-[10px] text-blue-500 ml-1">
                                                    {lang === "fr"
                                                      ? `(${part.texte.length} signes retirés)`
                                                      : `(${part.texte.length} chars removed)`}
                                                  </span>
                                                )}
                                              </span>
                                            );
                                          }
                                          return (
                                            <span
                                              key={i}
                                              className="text-[#1e3a8a] font-semibold"
                                              data-testid={`merged-hand-added-${mi}-${i}`}
                                              title={lang === "fr"
                                                ? "Écrit à la main"
                                                : "Written by hand"}
                                            >
                                              {part.texte}
                                            </span>
                                          );
                                        });
                                      }
                                      // Vincent B753 (Eric 2026-02-17) —
                                      // Non-éditeurs : rendu propre du texte
                                      // fusionné (résultat final), pas de
                                      // marques de correction rose/bleu.
                                      if (!canEdit) return text;
                                      const corrections = (seg.corrections || []).filter(c => c.correction && !c.rejected);
                                      if (!corrections.length) return text;
                                      // Vincent (2026-05-12) — bordeaux = proposé (par défaut)
                                      // bleu = accepté explicitement par l'utilisateur.
                                      // Build a lookup: correction-text → first non-rejected match (with its accepted flag).
                                      const byText = new Map();
                                      for (const c of corrections) {
                                        if (!byText.has(c.correction)) byText.set(c.correction, c);
                                      }
                                      const escaped = corrections
                                        .map(c => c.correction)
                                        .sort((a, b) => b.length - a.length)
                                        .map(s => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
                                      const re = new RegExp(`(${escaped.join("|")})`, "g");
                                      const parts = text.split(re);
                                      return parts.map((p, i) => {
                                        if (i % 2 === 0) return <span key={i}>{p}</span>;
                                        const c = byText.get(p);
                                        const isAccepted = !!(c && c.accepted);
                                        const cls = isAccepted
                                          ? "bg-blue-100 text-blue-800 font-semibold px-0.5 rounded"
                                          : "bg-rose-100 text-rose-900 font-semibold px-0.5 rounded";
                                        return (
                                          <mark
                                            key={i}
                                            className={cls}
                                            data-testid={`merged-correction-${mi}-${i}`}
                                            data-state={isAccepted ? "accepted" : "proposed"}
                                            title={isAccepted ? "Modification acceptée" : "Modification proposée"}
                                          >
                                            {p}
                                          </mark>
                                        );
                                      });
                                    })()}
                                    </span>
                                    {/* Tâche 1349 — L'étiquette passe SOUS le
                                        texte : en tête de ligne, elle décalait
                                        la première phrase du tour. */}
                                    {seg.manual_edit && (
                                      <div
                                        className="mt-1 text-[10px] font-semibold text-[#1e3a8a]"
                                        data-testid={`merged-manual-edit-badge-${mi}`}
                                        title={`${lang === "fr" ? "Phrase corrigée à la main par" : "Sentence hand-edited by"} ${
                                          seg.manual_edit.by || "?"
                                        } — ${String(seg.manual_edit.at || "").slice(0, 16).replace("T", " ")}`}
                                      >
                                        {lang === "fr" ? "corrigée à la main" : "hand-edited"}
                                      </div>
                                    )}
                                  </div>
                                  )}
                                </div>
                                {/* Vincent B943 (Eric 2026-02-14) — Encadré correction factuelle. */}
                                <WebCorrectionBadge corrections={seg.web_corrections} lang={lang} />
                                {/* Vincent B753 (Eric 2026-02-17) — Non-éditeurs
                                    ne voient PAS le bloc détaillé des corrections
                                    (originaux barrés, boutons ✓/✗/✎). */}
                                {canEdit && seg.is_modified && seg.corrections?.length > 0 && (
                                  <div className="ml-[226px] -mt-0.5 mb-2 space-y-1" data-testid={`corr-block-${seg.id}`}>
                                    {/* Vincent B882 (Eric 2026-02-14 SPEC STRICK PROD) —
                                        Dédup défensive au render : si l'historique du
                                        segment contient plusieurs entrées (original,
                                        correction) identiques (cycle rejet + relance
                                        « Orthographe » avant le fix backend), on ne
                                        garde que la 1ère occurrence pour éviter des
                                        doublons de lignes ✗/✎/✓.
                                        Vincent B951 (Eric 2026-02-14 SPEC STRICK PROD) —
                                        Tri par position `original` dans le texte du
                                        segment (ordre de lecture logique) au lieu de
                                        l'ordre d'application (souvent inversé pour ne
                                        pas décaler les indices). */}
                                    {seg.corrections.filter((c, ci, arr) =>
                                      arr.findIndex(x => x.original === c.original && x.correction === c.correction) === ci
                                    ).slice().sort((a, b) => {
                                      const txt = seg.merged_text || seg.text || "";
                                      const ai = txt.indexOf(a.original || "");
                                      const bi = txt.indexOf(b.original || "");
                                      // Les originals introuvables (déjà remplacés) restent à la fin.
                                      const aa = ai === -1 ? Infinity : ai;
                                      const bb = bi === -1 ? Infinity : bi;
                                      return aa - bb;
                                    }).map((c, ci) => {
                                      const cKey = `${seg.id}::${c.original}::${c.correction}`;
                                      const isRejected = rejectedCorrections.includes(cKey);
                                      const isAccepted = !!c.accepted && !isRejected;
                                      const isEditing = editingCorrection?.cKey === cKey;
                                      const rowStateCls = isRejected
                                        ? "opacity-40"
                                        : isAccepted
                                          ? "text-blue-700"
                                          : "text-zinc-600";
                                      // Vincent XLSX 2026-07-15 Row 488 (Eric 2026-07-16 SPEC STRICK) —
                                      // Ordre des 3 boutons INVERSÉ : ✗ (rejet) à gauche, ✎ (édition) au
                                      // milieu, ✓ (acceptation) à droite. C'est plus logique puisque le
                                      // texte original est à gauche (à rejeter) et le texte corrigé à
                                      // droite (à accepter). Grid reste identique, seul l'ordre change.
                                      return (
                                        <React.Fragment key={ci}>
                                        <div
                                          className={`grid grid-cols-[1fr_28px_28px_28px_1fr] gap-1.5 items-center text-[10.5px] ${rowStateCls}`}
                                          data-testid={`corr-row-${seg.id}-${ci}`}
                                        >
                                          {/* Col 1 : texte original (aligné à droite) */}
                                          <span
                                            className={`text-right line-through font-mono ${isRejected ? "" : "text-zinc-500"}`}
                                            title={c.reason ? `Raison : ${c.reason}` : undefined}
                                            data-testid={`corr-original-${seg.id}-${ci}`}
                                          >
                                            {c.original}
                                          </span>
                                          {/* Col 2 : ✗ refuser (ou ↶ restaurer) — MAINTENANT À GAUCHE */}
                                          <button
                                            type="button"
                                            disabled={!canEdit}
                                            onClick={async () => {
                                              if (isRejected) {
                                                try {
                                                  await axios.post(`${api}/api/videos/${videoId}/restore-correction`, {
                                                    segment_id: seg.id,
                                                    original: c.original,
                                                    correction: c.correction,
                                                  }, { withCredentials: true });
                                                  setRejectedCorrections((prev) => prev.filter((k) => k !== cKey));
                                                  toast.success(lang === "fr" ? "Correction restaurée" : "Correction restored");
                                                  loadAllTranscripts(seg.id);
                                                } catch (err) {
                                                  const detail = err?.response?.data?.detail || err?.message || (lang === "fr" ? "Erreur inconnue" : "Unknown error");
                                                  toast.error(lang === "fr" ? `Restauration échouée : ${detail}` : `Restore failed: ${detail}`);
                                                }
                                                return;
                                              }
                                              try {
                                                await axios.post(`${api}/api/videos/${videoId}/reject-correction`, {
                                                  segment_id: seg.id,
                                                  original: c.original,
                                                  correction: c.correction,
                                                }, { withCredentials: true });
                                                setRejectedCorrections((prev) => [...prev, cKey]);
                                                loadAllTranscripts(seg.id);
                                                // Vincent B936 rev2 (Eric 2026-02-26 SPEC STRICK PROD) —
                                                // Le toast avec action-button (B883) est remplacé
                                                // par une checkbox INLINE sous la correction rejetée.
                                                // Voir `applyAllProposals` et le bloc de rendu plus bas.
                                                const others = (allTranscripts?.merged?.segments || []).filter((s2) => {
                                                  if (s2.id === seg.id) return false;
                                                  return (s2.corrections || []).some(
                                                    (cc) =>
                                                      cc.original === c.original &&
                                                      cc.correction === c.correction &&
                                                      !cc.rejected
                                                  );
                                                }).length;
                                                if (others > 0) {
                                                  setApplyAllProposals((prev) => ({
                                                    ...prev,
                                                    [cKey]: { mode: "reject", others },
                                                  }));
                                                }
                                              } catch (err) {
                                                // Vincent B878 (Eric 2026-02-14 SPEC STRICK PROD) — Toast enrichi
                                                // du vrai détail HTTP pour ne plus perdre le diagnostic (403 CSRF,
                                                // 404 org mismatch, 500 …). Voir /app/memory/CHANGELOG.md.
                                                const detail = err?.response?.data?.detail || err?.message || (lang === "fr" ? "Erreur inconnue" : "Unknown error");
                                                toast.error(lang === "fr" ? `Rejet échoué : ${detail}` : `Reject failed: ${detail}`);
                                              }
                                            }}
                                            className={`justify-self-center w-6 h-6 rounded inline-flex items-center justify-center text-[12px] font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                                              isRejected
                                                ? "bg-zinc-200 text-zinc-700 hover:bg-zinc-300"
                                                : "border border-rose-300 text-rose-700 hover:bg-rose-50"
                                            }`}
                                            title={isRejected
                                              ? (lang === "fr" ? "Restaurer cette correction" : "Restore")
                                              : (lang === "fr" ? "Refuser (revenir à l'original)" : "Reject (revert to original)")}
                                            data-testid={`corr-reject-${seg.id}-${ci}`}
                                          >
                                            {isRejected ? "↶" : "✗"}
                                          </button>
                                          {/* Col 3 : ✎ éditer (pen) — reste au milieu */}
                                          <button
                                            type="button"
                                            disabled={!canEdit || isRejected}
                                            onClick={() => {
                                              setEditingCorrection(isEditing ? null : { cKey, draft: c.correction });
                                            }}
                                            className={`justify-self-center w-6 h-6 rounded inline-flex items-center justify-center text-[12px] transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                                              isEditing
                                                ? "bg-amber-500 text-white hover:bg-amber-600"
                                                : "border border-amber-300 text-amber-700 hover:bg-amber-50"
                                            }`}
                                            title={lang === "fr" ? "Réviser le texte de la correction" : "Edit correction text"}
                                            data-testid={`corr-edit-${seg.id}-${ci}`}
                                          >
                                            ✎
                                          </button>
                                          {/* Col 4 : ✓ accepter — MAINTENANT À DROITE */}
                                          <button
                                            type="button"
                                            disabled={!canEdit || isRejected}
                                            onClick={async () => {
                                              try {
                                                const endpoint = isAccepted ? "unaccept-correction" : "accept-correction";
                                                await axios.post(`${api}/api/videos/${videoId}/${endpoint}`, {
                                                  segment_id: seg.id,
                                                  original: c.original,
                                                  correction: c.correction,
                                                }, { withCredentials: true });
                                                toast.success(isAccepted
                                                  ? (lang === "fr" ? "Acceptation retirée" : "Acceptance removed")
                                                  : (lang === "fr" ? "Correction acceptée" : "Correction accepted"));
                                                loadAllTranscripts(seg.id);
                                                // Tâche 1342 — Enchaînement sur
                                                // la correction suivante (la
                                                // barre de la fusion décide via
                                                // « Relecture guidée »). Retirer
                                                // une acceptation n'enchaîne
                                                // pas : on est en train de
                                                // revenir en arrière.
                                                if (!isAccepted) {
                                                  window.dispatchEvent(new CustomEvent("knoria:merged-correction-accepted", {
                                                    detail: { videoId, segmentId: seg.id },
                                                  }));
                                                  // Vincent B936 rev2 — Case à cocher
                                                  // INLINE sous la correction acceptée
                                                  // quand le même couple existe ailleurs.
                                                  const others = (allTranscripts?.merged?.segments || []).filter((s2) => {
                                                    if (s2.id === seg.id) return false;
                                                    return (s2.corrections || []).some(
                                                      (cc) =>
                                                        cc.original === c.original &&
                                                        cc.correction === c.correction &&
                                                        !cc.accepted
                                                    );
                                                  }).length;
                                                  if (others > 0) {
                                                    setApplyAllProposals((prev) => ({
                                                      ...prev,
                                                      [cKey]: { mode: "accept", others },
                                                    }));
                                                  }
                                                } else {
                                                  // Unaccept → retire l'éventuelle checkbox résiduelle.
                                                  setApplyAllProposals((prev) => {
                                                    if (!prev[cKey]) return prev;
                                                    const next = { ...prev };
                                                    delete next[cKey];
                                                    return next;
                                                  });
                                                }
                                              } catch (err) {
                                                // Vincent B878 (Eric 2026-02-14 SPEC STRICK PROD) — Toast enrichi
                                                // du vrai détail HTTP pour Vincent (« Échec » nu masquait toute
                                                // info sur la cause : 403 CSRF, 404 org mismatch, 500 …).
                                                const detail = err?.response?.data?.detail || err?.message || (lang === "fr" ? "Erreur inconnue" : "Unknown error");
                                                toast.error(lang === "fr" ? `Échec : ${detail}` : `Failed: ${detail}`);
                                              }
                                            }}
                                            className={`justify-self-center w-6 h-6 rounded inline-flex items-center justify-center text-[12px] font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                                              isAccepted
                                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                                : "border border-blue-300 text-blue-600 hover:bg-blue-50"
                                            }`}
                                            title={isAccepted
                                              ? (lang === "fr" ? "Retirer l'acceptation" : "Remove acceptance")
                                              : (lang === "fr" ? "Accepter cette modification" : "Accept this modification")}
                                            data-testid={`corr-accept-${seg.id}-${ci}`}
                                          >
                                            ✓
                                          </button>
                                          {/* Col 5 : texte révisé (aligné à gauche) — édition inline si actif */}
                                          {isEditing ? (
                                            <div className="flex flex-col gap-1">
                                              <div className="flex items-center gap-1">
                                              <input
                                                autoFocus
                                                value={editingCorrection.draft}
                                                onChange={(e) => setEditingCorrection({ cKey, draft: e.target.value, applyAll: editingCorrection.applyAll })}
                                                onKeyDown={(e) => {
                                                  if (e.key === "Escape") setEditingCorrection(null);
                                                }}
                                                className="flex-1 text-[11px] px-1.5 py-0.5 border border-amber-400 rounded font-mono text-zinc-900 bg-amber-50"
                                                data-testid={`corr-edit-input-${seg.id}-${ci}`}
                                              />
                                              <button
                                                type="button"
                                                onClick={async () => {
                                                  const next = (editingCorrection.draft || "").trim();
                                                  if (!next || next === c.correction) {
                                                    setEditingCorrection(null);
                                                    return;
                                                  }
                                                  const applyAll = !!editingCorrection.applyAll;
                                                  try {
                                                    if (applyAll) {
                                                      // Vincent B936 — édition globale : tous les
                                                      // segments identiques reçoivent la nouvelle valeur.
                                                      const r = await axios.post(`${api}/api/videos/${videoId}/edit-correction-global`, {
                                                        original: c.original,
                                                        correction: c.correction,
                                                        new_correction: next,
                                                      }, { withCredentials: true });
                                                      const updated = r?.data?.segments_updated ?? 0;
                                                      toast.success(lang === "fr"
                                                        ? `Correction révisée sur ${updated} segment(s).`
                                                        : `Correction edited on ${updated} segment(s).`);
                                                    } else {
                                                      await axios.post(`${api}/api/videos/${videoId}/edit-correction`, {
                                                        segment_id: seg.id,
                                                        original: c.original,
                                                        correction: c.correction,
                                                        new_correction: next,
                                                      }, { withCredentials: true });
                                                      toast.success(lang === "fr" ? "Correction révisée" : "Correction edited");
                                                    }
                                                    setEditingCorrection(null);
                                                    loadAllTranscripts(seg.id);
                                                  } catch (err) {
                                                    toast.error(err.response?.data?.detail || (lang === "fr" ? "Échec" : "Failed"));
                                                  }
                                                }}
                                                className="text-teal-700 hover:bg-teal-50 px-1.5 py-0.5 rounded text-[11px] font-semibold"
                                                data-testid={`corr-edit-save-${seg.id}-${ci}`}
                                              >💾</button>
                                              <button
                                                type="button"
                                                onClick={() => setEditingCorrection(null)}
                                                className="text-zinc-500 hover:bg-zinc-100 px-1.5 py-0.5 rounded text-[11px]"
                                                data-testid={`corr-edit-cancel-${seg.id}-${ci}`}
                                              >✕</button>
                                              </div>
                                              {/* Vincent B936 (Eric 2026-02-14 SPEC STRICK PROD) —
                                                  Toggle « Appliquer partout (N occurrences) » : quand
                                                  le même couple (original, correction) apparaît sur
                                                  N > 0 autres segments, on propose d'appliquer la
                                                  révision partout d'un seul coup. Case à cocher AVANT
                                                  le save (option b user).*/}
                                              {(() => {
                                                const others = (allTranscripts?.merged?.segments || []).filter((s2) => {
                                                  if (s2.id === seg.id) return false;
                                                  return (s2.corrections || []).some(
                                                    (cc) => cc.original === c.original && cc.correction === c.correction,
                                                  );
                                                }).length;
                                                if (others === 0) return null;
                                                return (
                                                  <label
                                                    className="flex items-center gap-1.5 text-[10px] text-zinc-600 cursor-pointer"
                                                    data-testid={`corr-edit-apply-all-label-${seg.id}-${ci}`}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      checked={!!editingCorrection.applyAll}
                                                      onChange={(e) => setEditingCorrection({
                                                        cKey,
                                                        draft: editingCorrection.draft,
                                                        applyAll: e.target.checked,
                                                      })}
                                                      className="h-3 w-3 accent-teal-600"
                                                      data-testid={`corr-edit-apply-all-${seg.id}-${ci}`}
                                                    />
                                                    <span>
                                                      {lang === "fr"
                                                        ? `Appliquer partout (${others} occurrence${others > 1 ? "s" : ""})`
                                                        : `Apply everywhere (${others} occurrence${others > 1 ? "s" : ""})`}
                                                    </span>
                                                  </label>
                                                );
                                              })()}
                                            </div>
                                          ) : (
                                            <span
                                              className={`text-left font-mono ${
                                                isRejected
                                                  ? "line-through text-zinc-400"
                                                  : isAccepted
                                                    ? "text-blue-800 font-semibold"
                                                    : "text-rose-800 font-medium"
                                              }`}
                                              title={c.user_edited ? (lang === "fr" ? "Modifié par l'utilisateur" : "User-edited") : (c.reason || undefined)}
                                              data-testid={`corr-revised-${seg.id}-${ci}`}
                                            >
                                              {c.correction}
                                              {c.user_edited && <span className="ml-1 text-[8px] text-amber-600">✎</span>}
                                            </span>
                                          )}
                                        </div>
                                        {/* Vincent B936 rev2 (Eric 2026-02-26 SPEC STRICK PROD) —
                                            Checkbox INLINE « Appliquer partout (N occurrences) »
                                            affichée sous la correction quand un couple (original,
                                            correction) est accepté ou rejeté ET qu'il existe des
                                            doublons sur d'autres segments. Remplace l'ancien toast
                                            avec action-button (B883/B884) que Vincent trouvait trop
                                            volatile (le toast disparaissait avant que Vincent ait
                                            eu le temps de cliquer). */}
                                        {applyAllProposals[cKey] && !isEditing && (
                                          <div
                                            className={`col-span-full ml-2 mt-0.5 mb-0.5 pl-3 border-l-2 ${
                                              applyAllProposals[cKey].mode === "accept"
                                                ? "border-blue-300 bg-blue-50/50"
                                                : "border-rose-300 bg-rose-50/50"
                                            } rounded-r px-2 py-1 flex items-center gap-2`}
                                            data-testid={`corr-apply-all-row-${seg.id}-${ci}`}
                                            data-apply-all-mode={applyAllProposals[cKey].mode}
                                          >
                                            <label className="flex items-center gap-1.5 text-[10.5px] cursor-pointer text-zinc-800 font-medium">
                                              <input
                                                type="checkbox"
                                                disabled={!!applyAllBusy[cKey]}
                                                onChange={async (e) => {
                                                  if (!e.target.checked) return;
                                                  const proposal = applyAllProposals[cKey];
                                                  if (!proposal) return;
                                                  setApplyAllBusy((prev) => ({ ...prev, [cKey]: true }));
                                                  try {
                                                    const endpoint = proposal.mode === "accept"
                                                      ? "accept-correction-global"
                                                      : "reject-correction-global";
                                                    const r = await axios.post(
                                                      `${api}/api/videos/${videoId}/${endpoint}`,
                                                      { original: c.original, correction: c.correction },
                                                      { withCredentials: true }
                                                    );
                                                    const updated = r?.data?.segments_updated ?? 0;
                                                    toast.success(
                                                      proposal.mode === "accept"
                                                        ? (lang === "fr"
                                                            ? `Accepté sur ${updated} segment(s) supplémentaire(s).`
                                                            : `Accepted on ${updated} more segment(s).`)
                                                        : (lang === "fr"
                                                            ? `Rejeté sur ${updated} segment(s) supplémentaire(s).`
                                                            : `Rejected on ${updated} more segment(s).`)
                                                    );
                                                    loadAllTranscripts(seg.id);
                                                    setApplyAllProposals((prev) => {
                                                      const next = { ...prev };
                                                      delete next[cKey];
                                                      return next;
                                                    });
                                                  } catch (err) {
                                                    const d = err?.response?.data?.detail || err?.message || (lang === "fr" ? "Erreur inconnue" : "Unknown error");
                                                    toast.error(lang === "fr"
                                                      ? `Application globale échouée : ${d}`
                                                      : `Global apply failed: ${d}`);
                                                  } finally {
                                                    setApplyAllBusy((prev) => {
                                                      const next = { ...prev };
                                                      delete next[cKey];
                                                      return next;
                                                    });
                                                  }
                                                }}
                                                className={`h-3 w-3 ${
                                                  applyAllProposals[cKey].mode === "accept"
                                                    ? "accent-blue-600"
                                                    : "accent-rose-600"
                                                }`}
                                                data-testid={`corr-apply-all-checkbox-${seg.id}-${ci}`}
                                              />
                                              <span>
                                                {applyAllBusy[cKey]
                                                  ? (lang === "fr" ? "Application en cours…" : "Applying…")
                                                  : (applyAllProposals[cKey].mode === "accept"
                                                      ? (lang === "fr"
                                                          ? `☑ Accepter partout (${applyAllProposals[cKey].others} occurrence${applyAllProposals[cKey].others > 1 ? "s" : ""})`
                                                          : `☑ Accept everywhere (${applyAllProposals[cKey].others} occurrence${applyAllProposals[cKey].others > 1 ? "s" : ""})`)
                                                      : (lang === "fr"
                                                          ? `☑ Rejeter partout (${applyAllProposals[cKey].others} occurrence${applyAllProposals[cKey].others > 1 ? "s" : ""})`
                                                          : `☑ Reject everywhere (${applyAllProposals[cKey].others} occurrence${applyAllProposals[cKey].others > 1 ? "s" : ""})`))}
                                              </span>
                                            </label>
                                            <button
                                              type="button"
                                              onClick={() => setApplyAllProposals((prev) => {
                                                const next = { ...prev };
                                                delete next[cKey];
                                                return next;
                                              })}
                                              className="ml-auto text-[10px] text-zinc-500 hover:text-zinc-800 hover:underline"
                                              data-testid={`corr-apply-all-dismiss-${seg.id}-${ci}`}
                                              title={lang === "fr" ? "Ignorer et fermer" : "Dismiss"}
                                            >
                                              ✕
                                            </button>
                                          </div>
                                        )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {/* Tâche 1175 — Passages vus par Fireflies seul :
                              ils n'existent pas dans la fusion (elle part des
                              segments Whisper), donc on les rend ici en
                              LECTURE SEULE, clairement marqués. */}
                          {sourceFilter === "fireflies" && firefliesOnlyGaps.length > 0 && (
                            <div data-testid="merged-fireflies-only-gaps">
                              <p className="text-[10px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1 my-2">
                                {lang === "fr"
                                  ? `${firefliesOnlyGaps.length} passage(s) entendu(s) par Fireflies seul — absents de la fusion, donc jamais couverts par Whisper.`
                                  : `${firefliesOnlyGaps.length} passage(s) heard by Fireflies only — absent from the fusion, so never covered by Whisper.`}
                              </p>
                              {firefliesOnlyGaps.map((seg, gi) => (
                                <div
                                  key={`ff-gap-${gi}`}
                                  className="grid grid-cols-[70px_90px_1fr] gap-3 items-start py-1.5 cursor-pointer rounded hover:bg-violet-50/60"
                                  onClick={() => seekTo(seg.start)}
                                  data-testid={`merged-fireflies-only-gap-${gi}`}
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); seekTo(seg.start); }}
                                    className="text-[11px] font-mono text-left tabular-nums leading-relaxed text-zinc-400 hover:text-[#002FA7] inline-flex items-center gap-1"
                                    data-testid={`merged-fireflies-only-gap-timecode-${gi}`}
                                    data-source="fireflies"
                                  >
                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${SEGMENT_SOURCE_META.fireflies.dot}`} aria-hidden="true" />
                                    {formatTimeBracket(seg.start)}
                                  </button>
                                  <div
                                    className="text-[11px] font-semibold leading-tight break-words"
                                    style={{ color: speakerMap[seg.speaker] || "#71717a" }}
                                  >
                                    {formatSpeakerLines(seg.speaker).map((line, li) => (
                                      <div key={li}>{line}</div>
                                    ))}
                                  </div>
                                  <span className="text-sm leading-relaxed text-zinc-700 whitespace-pre-wrap">
                                    {seg.text || ""}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Eric / Vincent (2026-05-13) — Le rapport d'analyse de fusion a été
                            déplacé dans une modale dédiée (bouton « Rapport de fusion » à côté
                            du dropdown « Source de la transcription »). Ainsi, le transcript
                            n'est plus pollué par la méta-information de fusion. */}
                      </div>
                    )}
                  </div>
  );
};

export default MergedSubTab;
